import { getJWTToken } from '../../utils/auth';
import type { FlashlightBenchmarkYear } from './flashlightBenchmarks';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type PlanStatus = 'draft' | 'finalized';
export type AccommodationList = 'classroom' | 'contentTests' | 'access';
export type AccommodationSource = 'suggested' | 'carried' | 'added';
export type AccommodationStatus = 'proposed' | 'confirmed' | 'removed';
export type ReviewerStatus = 'reviewed' | 'earlier' | 'not_yet';
export type SignOffRule = 'el_teacher_only' | 'every_teacher';
export type PlanStanding = 'active' | 'monitoring' | 'former' | 'waived' | 'departed' | 'unknown';

export type PlanRecordAuthor = { name: string };

export type PlanRecordYear = { year: string; status: PlanStatus; studentId?: string };

export type PlanRecordVersion = {
  number: number;
  status: PlanStatus;
  draftSource: 'generated' | 'revised';
  draftedAt: string;
  finalizedAt: string;
  finalizedBy: PlanRecordAuthor | null;
  revisedFrom: string;
  revisedWhat: string;
  generationVersion: number;
  generated: boolean;
  revision: number;
};

export type PlanRecordContent = { generationVersion: number; generatedAt: string; body: unknown };

export type PlanSnapshotEdit = { key: string; text: string; generatedText: string; by: PlanRecordAuthor; at: string };
export type PlanSupportChange = { text: string; by: PlanRecordAuthor; at: string };

export type PlanAccommodation = {
  list: AccommodationList;
  itemId: string;
  label: string;
  source: AccommodationSource;
  status: AccommodationStatus;
  by: PlanRecordAuthor | null;
  at: string;
  fromYear: string;
  noClassroomMatch: boolean;
};

export type PlanNote = {
  id: string;
  text: string;
  author: PlanRecordAuthor;
  role: string;
  createdAt: string;
  schoolYearWritten: string;
  standingWritten?: string;
};

export type PlanSignOff = { name: string; version: number; signedAt: string };
export type PlanReviewer = { name: string; role: string; status: ReviewerStatus; signedAt: string; version: number };

export type CatalogItem = { id: string; label: string; matchesClassroomItem?: string; requiresIepOr504?: boolean };
export type PlanCatalog = { classroom: CatalogItem[]; contentTests: CatalogItem[]; access: CatalogItem[] };

export type PlanIep = { sis: boolean | null; section504: boolean | null; access: boolean | null };

export type PlanFlashlight = {
  status: 'saved' | 'unavailable';
  asOf: string;
  scheme?: { score: number; label: string; color: string }[];
  years: FlashlightBenchmarkYear[];
};

export type PlanRecord = {
  studentId?: string;
  schoolYears: PlanRecordYear[];
  year: string;
  currentYear: string;
  isCurrentYear: boolean;
  version: PlanRecordVersion;
  content: PlanRecordContent | null;
  snapshotEdits: PlanSnapshotEdit[];
  supportsRemoved: PlanSupportChange[];
  supportsAdded: PlanSupportChange[];
  accommodations: PlanAccommodation[];
  accommodationsSaved: boolean;
  notes: PlanNote[];
  signOffs: PlanSignOff[];
  expectedReviewers: PlanReviewer[];
  alsoReviewed: PlanSignOff[];
  mySignOff: PlanSignOff | null;
  rosteringKnown: boolean;
  iep: PlanIep;
  alternateAccess: boolean;
  catalog: PlanCatalog | null;
  signOffRule: SignOffRule;
  role: 'admin' | 'teacher';
  standing: PlanStanding;
  notesReadOnly: boolean;
  canEdit: boolean;
  canFinalize: boolean;
  canRevise: boolean;
  canSignOff: boolean;
  flashlight: PlanFlashlight | null;
};

export type SnapshotEditInput = { key: string; text: string };
export type SnapshotSaveInput = { edits: SnapshotEditInput[]; supportsRemoved: string[]; supportsAdded: string[] };
export type AccommodationInput = { list: AccommodationList; itemId: string; on: boolean; source?: AccommodationSource };

export class PlanRecordConflictError extends Error {
  code: string;
  detail: Record<string, unknown>;

  constructor(code: string, detail: Record<string, unknown>) {
    super(`plan record conflict: ${code}`);
    this.name = 'PlanRecordConflictError';
    this.code = code;
    this.detail = detail;
  }
}

export class PlanRecordForbiddenError extends Error {
  readonly code: string;
  constructor(code: string) {
    super(`plan-record fetch failed: 403 (${code})`);
    this.name = 'PlanRecordForbiddenError';
    this.code = code;
  }
}

export class PlanRecordNotFoundError extends Error {
  code: string;

  constructor(code: string) {
    super(`plan record not found: ${code}`);
    this.name = 'PlanRecordNotFoundError';
    this.code = code;
  }
}

const STANDINGS: PlanStanding[] = ['active', 'monitoring', 'former', 'waived', 'departed', 'unknown'];

function standing(value: unknown): PlanStanding {
  return STANDINGS.find((s) => s === value) ?? 'active';
}

function list<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function author(value: unknown): PlanRecordAuthor | null {
  const a = value as { name?: unknown } | null | undefined;
  return a && typeof a.name === 'string' ? { name: a.name } : null;
}

function tri(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

export function parsePlanRecord(raw: unknown): PlanRecord {
  const r = (raw ?? {}) as Record<string, unknown>;
  const v = (r.version ?? {}) as Record<string, unknown>;
  const iep = (r.iep ?? {}) as Record<string, unknown>;
  const content = r.content as Record<string, unknown> | null | undefined;
  const catalog = r.catalog as Record<string, unknown> | null | undefined;
  return {
    studentId: typeof r.studentId === 'string' && r.studentId ? r.studentId : undefined,
    schoolYears: list<PlanRecordYear>(r.schoolYears),
    year: str(r.year),
    currentYear: str(r.currentYear),
    isCurrentYear: r.isCurrentYear === true,
    version: {
      number: typeof v.number === 'number' ? v.number : 0,
      status: v.status === 'finalized' ? 'finalized' : 'draft',
      draftSource: v.draftSource === 'revised' ? 'revised' : 'generated',
      draftedAt: str(v.draftedAt),
      finalizedAt: str(v.finalizedAt),
      finalizedBy: author(v.finalizedBy),
      revisedFrom: str(v.revisedFrom),
      revisedWhat: str(v.revisedWhat),
      generationVersion: typeof v.generationVersion === 'number' ? v.generationVersion : 0,
      generated: v.generated === true,
      revision: typeof v.revision === 'number' ? v.revision : 0,
    },
    content: content ? { generationVersion: typeof content.generationVersion === 'number' ? content.generationVersion : 0, generatedAt: str(content.generatedAt), body: content.body ?? null } : null,
    snapshotEdits: list<PlanSnapshotEdit>(r.snapshotEdits),
    supportsRemoved: list<PlanSupportChange>(r.supportsRemoved),
    supportsAdded: list<PlanSupportChange>(r.supportsAdded),
    accommodations: list<Record<string, unknown>>(r.accommodations).map((a) => ({
      list: a.list as AccommodationList,
      itemId: str(a.itemId),
      label: str(a.label),
      source: a.source as AccommodationSource,
      status: a.status as AccommodationStatus,
      by: author(a.by),
      at: str(a.at),
      fromYear: str(a.fromYear),
      noClassroomMatch: a.noClassroomMatch === true,
    })),
    accommodationsSaved: r.accommodationsSaved === true,
    notes: list<PlanNote>(r.notes),
    signOffs: list<PlanSignOff>(r.signOffs),
    expectedReviewers: list<Record<string, unknown>>(r.expectedReviewers).map((e) => ({
      name: str(e.name),
      role: str(e.role),
      status: (e.status as ReviewerStatus) ?? 'not_yet',
      signedAt: str(e.signedAt),
      version: typeof e.version === 'number' ? e.version : 0,
    })),
    alsoReviewed: list<PlanSignOff>(r.alsoReviewed),
    mySignOff: r.mySignOff ? (r.mySignOff as PlanSignOff) : null,
    rosteringKnown: r.rosteringKnown === true,
    iep: { sis: tri(iep.sis), section504: tri(iep.section504), access: tri(iep.access) },
    alternateAccess: r.alternateAccess === true,
    catalog: catalog
      ? {
          classroom: list<CatalogItem>(catalog.classroom),
          contentTests: list<CatalogItem>(catalog.contentTests),
          access: list<CatalogItem>(catalog.access),
        }
      : null,
    signOffRule: r.signOffRule === 'every_teacher' ? 'every_teacher' : 'el_teacher_only',
    role: r.role === 'teacher' ? 'teacher' : 'admin',
    standing: standing(r.standing),
    notesReadOnly: r.notesReadOnly === true,
    canEdit: r.canEdit === true,
    canFinalize: r.canFinalize === true,
    canRevise: r.canRevise === true,
    canSignOff: r.canSignOff === true,
    flashlight: flashlightOf(r.flashlight),
  };
}

function flashlightOf(value: unknown): PlanFlashlight | null {
  const f = value as Record<string, unknown> | null | undefined;
  if (!f || typeof f !== 'object') return null;
  return {
    status: f.status === 'saved' ? 'saved' : 'unavailable',
    asOf: str(f.asOf),
    ...(Array.isArray(f.scheme) ? { scheme: f.scheme as PlanFlashlight['scheme'] } : {}),
    years: list<FlashlightBenchmarkYear>(f.years),
  };
}

function recordPath(studentId: string, suffix = ''): string {
  return `${apiUrl}/students/${encodeURIComponent(studentId)}/plan-record${suffix}`;
}

export async function fetchPlanRecord(studentId: string, schoolYear?: string): Promise<PlanRecord> {
  const token = await getJWTToken();
  const query = schoolYear ? `?schoolYear=${encodeURIComponent(schoolYear)}` : '';
  const response = await fetch(recordPath(studentId) + query, { headers: authHeaders(token) });
  if (response.status === 404 || response.status === 400) {
    const detail = ((await response.json().catch(() => ({}))) ?? {}) as Record<string, unknown>;
    throw new PlanRecordNotFoundError(typeof detail.error === 'string' ? detail.error : 'plan_not_found');
  }
  if (response.status === 403) {
    const detail = ((await response.json().catch(() => ({}))) ?? {}) as Record<string, unknown>;
    throw new PlanRecordForbiddenError(typeof detail.error === 'string' ? detail.error : 'forbidden');
  }
  if (!response.ok) {
    throw new Error(`plan-record fetch failed: ${response.status}`);
  }
  return parsePlanRecord(await response.json());
}

async function write(studentId: string, suffix: string, method: 'PUT' | 'POST', body: Record<string, unknown>): Promise<PlanRecord> {
  const token = await getJWTToken();
  const response = await fetch(recordPath(studentId, suffix), {
    method,
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (response.status === 409) {
    const detail = ((await response.json().catch(() => ({}))) ?? {}) as Record<string, unknown>;
    throw new PlanRecordConflictError(typeof detail.error === 'string' ? detail.error : 'conflict', detail);
  }
  if (!response.ok) {
    throw new Error(`${method} plan-record${suffix} failed: ${response.status}`);
  }
  return parsePlanRecord(await response.json());
}

export function savePlanSnapshot(studentId: string, schoolYear: string, input: SnapshotSaveInput, revision?: number): Promise<PlanRecord> {
  return write(studentId, '/snapshot', 'PUT', { schoolYear, revision, ...input });
}

export function savePlanAccommodations(studentId: string, schoolYear: string, items: AccommodationInput[], revision?: number): Promise<PlanRecord> {
  return write(studentId, '/accommodations', 'PUT', { schoolYear, revision, items });
}

export function finalizePlan(studentId: string, schoolYear: string, generationVersion: number, revision?: number): Promise<PlanRecord> {
  return write(studentId, '/finalize', 'POST', { schoolYear, generationVersion, revision });
}

export function revisePlan(studentId: string, schoolYear: string): Promise<PlanRecord> {
  return write(studentId, '/revise', 'POST', { schoolYear });
}

export function signOffPlan(studentId: string, schoolYear: string, version: number): Promise<PlanRecord> {
  return write(studentId, '/sign-off', 'POST', { schoolYear, version });
}

export function addPlanNote(studentId: string, schoolYear: string, text: string): Promise<PlanRecord> {
  return write(studentId, '/notes', 'POST', { schoolYear, text });
}
